import { Directive, Renderer2, ElementRef } from '@angular/core';

@Directive({
    selector: '.btn:not(.btn-link),.card-image",.navbar a:not(.withoutripple),.footer a:not(.withoutripple),.dropdown-menu a,.nav-tabs a:not(.withoutripple),.withripple,.pagination li:not(.active):not(.disabled) a:not(.withoutripple)"'
})
export class RipplesDirective {
    private el: ElementRef;
    private rippleContainer: any;
    private ripple: any;
    private eventListeners: Map<string, any>;

    constructor(private elementRef: ElementRef, public renderer: Renderer2) {
        this.el = this.elementRef;

        this.rippleContainer = this.renderer.createElement("div");
        this.renderer.addClass(this.rippleContainer, "ripple-container");
        this.renderer.appendChild(this.el.nativeElement, this.rippleContainer);

        this.eventListeners = new Map<string, any>();

        this.init();
    }

    private init(): void {
        this.addEventListener(this.el.nativeElement, ["mousedown", "touchstart"], this.onMouseDown.bind(this));
    }

    private onMouseDown(event: MouseEvent): void {
        if (!event) // TODO Firefox bug
            return;

        if (this.rippleContainer.childNodes.length > 0)
            return;
        /**
         * Verify if the user is just touching on a device and return if so
         */
        if (RipplesDirective.isTouch() && event.type === "mousedown") {
            return;
        }

        /**
         * Get relY and relX positions
         */
        const relY = RipplesDirective.getRelY(this.rippleContainer, event);
        const relX = RipplesDirective.getRelX(this.rippleContainer, event);

        /**
         * If relY and/or relX are false, return the event
         */
        if (!relY && !relX) {
            return;
        }

        /**
         * Get the ripple color
         */
        const rippleColor = RipplesDirective.getRipplesColor(this.el.nativeElement);

        /**
         * Create the ripple element
         */
        this.ripple = this.renderer.createElement("div");

        /**
         * Append the ripple to the wrapper
         */
        this.renderer.appendChild(this.rippleContainer, this.ripple);

        this.renderer.addClass(this.ripple, "ripple");
        this.ripple.style["left"] = `${relX}px`;
        this.ripple.style["top"] = `${relY}px`;
        this.ripple.style["background-color"] = rippleColor;

        RipplesDirective.enforceStyleRecalculation(this.ripple);

        /**
         * Turn on the ripple animation
         */
        this.rippleOn(this.el.nativeElement, this.ripple);

        /**
         * Call the rippleEnd function when the transition "on" ends
         */
        setTimeout(() => {
            this.rippleEnd(this.ripple);
        }, 500);

        /**
         * Detect when the user leaves the element
         */
        this.addEventListener(this.el.nativeElement, ["mouseup", "mouseleave", "touchend"], () => {
            this.ripple.dataset["mousedown"] = "off";

            if (this.ripple.dataset["animating"] == "off") {
                this.rippleOut(this.ripple);
            }
        });
    }

    private static isTouch(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    private static hasTransitionSupport(): boolean {
        const thisBody = document.body != null ? document.body : document.documentElement;

        return thisBody.style['transition'] != null ||
            thisBody.style['WebkitTransition'] != null ||
            thisBody.style['MozTransition'] != null ||
            thisBody.style['MsTransition'] != null ||
            thisBody.style['OTransition'] != null;
    };

    private static getNewSize(element: HTMLElement, ripple: HTMLElement): number {
        return (Math.max(element.offsetWidth, element.offsetHeight) / ripple.offsetWidth) * 2.5;
    };

    private static getRipplesColor(element: HTMLElement): any {
        return element.dataset["ripple-color"] ? element.dataset["ripple-color"] : window.getComputedStyle(element).color;
    };

    private static getRelX(element: HTMLElement, event: any): number {
        const offset = element.getBoundingClientRect();

        if (!RipplesDirective.isTouch()) {
            /**
             * Get the mouse position relative to the ripple wrapper
             */
            return event.pageX - offset.left;
        } else {
            /**
             * Make sure the user is using only one finger and then get the touch
             * position relative to the ripple wrapper
             */
            event = event.originalEvent;

            if (event != null && event.touches != null && event.touches.length === 1) {
                return event.touches[0].pageX - offset.left;
            }

            return 0;
        }
    };

    /**
     * Get the relY
     */
    private static getRelY(element: HTMLElement, event: any): number {
        const offset = element.getBoundingClientRect();

        if (!RipplesDirective.isTouch()) {
            /**
             * Get the mouse position relative to the ripple wrapper
             */
            return event.pageY - offset.top;
        } else {
            /**
             * Make sure the user is using only one finger and then get the touch
             * position relative to the ripple wrapper
             */
            event = event.originalEvent;

            if (event != null && event.touches != null && event.touches.length === 1) {
                return event.touches[0].pageY - offset.top;
            }

            return 0;
        }
    };

    private rippleEnd(ripple: HTMLElement): void {
        ripple.dataset["animating"] = "off";

        if (ripple.dataset["mousedown"] == "off") {
            this.rippleOut(ripple);
        }
    };

    /**
     * Turn off the ripple effect
     */
    private rippleOut(ripple: HTMLElement): void {
        this.eventListeners.forEach((value: any, key: string) => {
            ripple.removeEventListener(key, value);
        });

        this.eventListeners.clear();

        if (RipplesDirective.hasTransitionSupport()) {
            this.renderer.addClass(ripple, "ripple-out");
        }

        this.addEventListener(ripple, ["transitionend", "webkitTransitionEnd", "oTransitionEnd", "MSTransitionEnd"], () => {
            ripple.parentNode.removeChild(ripple);
        });
    };

    private rippleOn(element: HTMLElement, ripple: HTMLElement): void {
        const size = RipplesDirective.getNewSize(element, ripple);

        if (RipplesDirective.hasTransitionSupport()) {
            this.renderer.addClass(ripple, "ripple-on");

            ripple.style["-ms-transform"] = `scale(${size})`;
            ripple.style["-moz-transform"] = `scale(${size})`;
            ripple.style["-webkit-transform"] = `scale(${size})`;
            ripple.style["transform"] = `scale(${size})`;

            ripple.dataset["animating"] = "on";
            ripple.dataset["mousedown"] = "on";
        }
    };

    private static enforceStyleRecalculation(element: HTMLElement): void {
        window.getComputedStyle(element).getPropertyValue('opacity');
    }

    private addEventListener(element: HTMLElement, events: string[], fn: any): void {
        for (const event of events) {
            element.addEventListener(event, fn);

            this.eventListeners.set(event, fn);
        }
    }
}


// WEBPACK FOOTER //
// ./app/common/directives/ripples.directive.ts